import {
  loadYouTubeAPI,
  getYT,
  YT_STATE,
  type YTPlayer,
} from '@/utils/youtube-api';

const POLL_MS = 500;
const API_FALLBACK_MS = 15_000;
const ONREADY_TIMEOUT_MS = 30_000;

type PollCtx = {
  threshold: number;
  fire: () => void;
  onProgress?: (elapsed: number, duration: number) => void;
};

function pollOnce(player: YTPlayer, watched: Set<number>, ctx: PollCtx): void {
  const t = player.getCurrentTime();
  const d = player.getDuration();
  if (!Number.isFinite(t) || !Number.isFinite(d) || d <= 0) return;
  ctx.onProgress?.(t, d);
  watched.add(Math.floor(t));
  if (watched.size / d >= ctx.threshold) ctx.fire();
}

export type AttachArgs = {
  iframe: HTMLIFrameElement;
  threshold: number;
  fire: () => void;
  onPlayer: (p: YTPlayer | null) => void;
  onPlaying: (v: boolean) => void;
  onProgress?: (elapsed: number, duration: number) => void;
};

type State = {
  p?: YTPlayer;
  pollId?: number;
  readyTimer: number;
  destroyed: boolean;
};

function startPolling(s: State, a: AttachArgs): void {
  if (!s.p) return;
  const watched = new Set<number>();
  const ctx: PollCtx = { threshold: a.threshold, fire: a.fire };
  if (a.onProgress) ctx.onProgress = a.onProgress;
  s.pollId = window.setInterval(() => pollOnce(s.p!, watched, ctx), POLL_MS);
}

function destroy(s: State, onPlayer: (p: null) => void): void {
  s.destroyed = true;
  window.clearTimeout(s.readyTimer);
  if (s.pollId !== undefined) clearInterval(s.pollId);
  s.p?.destroy();
  onPlayer(null);
}

export function attachPlayer(a: AttachArgs): () => void {
  const yt = getYT();
  if (!yt) return () => undefined;
  const s: State = {
    destroyed: false,
    readyTimer: window.setTimeout(a.fire, ONREADY_TIMEOUT_MS),
  };
  const onReady = (): void => {
    if (s.destroyed || !s.p) return;
    window.clearTimeout(s.readyTimer);
    a.onPlayer(s.p);
    startPolling(s, a);
  };
  try {
    s.p = new yt.Player(a.iframe, {
      events: {
        onReady,
        onStateChange: (ev) => a.onPlaying(ev.data === YT_STATE.PLAYING),
      },
    });
  } catch {
    /* readyTimer fallback fires fire() */
  }
  return () => destroy(s, a.onPlayer);
}

type SetupArgs = Omit<AttachArgs, 'iframe'> & {
  iframe: HTMLIFrameElement | null;
};

export function setupTracker(a: SetupArgs): () => void {
  const fb = window.setTimeout(a.fire, API_FALLBACK_MS);
  let cleanup: (() => void) | null = null;
  let aborted = false;
  void loadYouTubeAPI().then(() => {
    if (aborted) return;
    window.clearTimeout(fb);
    if (a.iframe) cleanup = attachPlayer({ ...a, iframe: a.iframe });
  });
  return () => {
    aborted = true;
    window.clearTimeout(fb);
    cleanup?.();
  };
}
